import React, { ChangeEvent, FormEvent, useState } from "react";
import {
  BadgeCheck,
  Dribbble,
  Facebook,
  Github,
  Globe,
  ImagePlus,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  MoreHorizontal,
  UserCircle,
} from "lucide-react";
import { Dropdown } from "Common/Components/Dropdown";
import { useParams } from "react-router-dom";

// IMage
import avatar1 from "assets/images/users/avatar-1.png";
import useUserProfile from "hooks/useUserProfile";
import { useMutation, useQueryClient } from "react-query";
import { sendUserMessage, verifyUser } from "services/user.service";
import Modal from "Common/Components/Modal";
import { KycStatus } from "types/user.type";

const AccountInfo = ({ className }: any) => {
  const [showMessage, setShowMessage] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [userStatus, setStatus] = useState<KycStatus>("In Review");
  const [comment, setComment] = useState("");
  const params = useParams();
  const { data, isLoading } = useUserProfile(params.id!);

  const queryClient = useQueryClient();

  const { isLoading: verifying, mutate } = useMutation(
    async (input: { status: string; comment: string }) =>
      await verifyUser(data?.data?.user?._id!, input),
    {
      onSuccess(data) {
        queryClient.invalidateQueries();
        setShowComment(false);
        alert(data.message);
      },
      onError(err: any) {
        alert(err?.response?.data?.message);
      },
    }
  );

  const { isLoading: messaging, mutate: sendMessage } = useMutation(
    async (input: { title: string; message: string }) =>
      await sendUserMessage(data?.data?.user?._id!, input),
    {
      onSuccess(data) {
        toggleShowMessage();
        alert(data.message);
      },
      onError(err: any) {
        alert(err?.response?.data?.message);
      },
    }
  );

  const handleVerification = (e: FormEvent) => {
    e.preventDefault();
    mutate({ status: userStatus, comment });
  };

  const toggleComment = () => {
    setShowComment(!showComment);
  };

  const toggleShowMessage = () => {
    setShowMessage(!showMessage);
  };

  const handleReject = () => {
    setStatus("Rejected");
    toggleComment();
  };

  const handleApprove = () => {
    setStatus("Approved");
    const confirm = window.confirm(
      `Sure to approve ${data?.data?.user?.firstName}  ${data?.data?.user?.lastName}`
    );
    if (confirm) {
      mutate({ status: "Approved", comment: "" });
    }
    // toggleComment();
  };

  const handleMessage = (e: FormEvent) => {
    e.preventDefault();

    // @ts-ignore
    const formData = new FormData(e.target);

    const title = formData.get("title") as string;
    const message = formData.get("message") as string;
    sendMessage({ title, message });
  };

  if (isLoading) return <p>Loading...</p>;

  return (
    <React.Fragment>
      <div>
        <div className="flex flex-col lg:flex-row justify-between px-6 lg:px-8">
          <div className=" flex p-4 gap-4 ">
            <div className="lg:col-span-2 2xl:col-span-1">
              <div className="relative inline-block size-20 rounded-full shadow-md bg-slate-100 profile-user xl:size-28">
                <img
                  src={data?.data?.profilePicture?.url ?? avatar1}
                  alt=""
                  className="object-cover border-0 rounded-full img-thumbnail user-profile-image h-full w-full"
                />
              </div>
            </div>
            <div className="lg:col-span-10 2xl:col-span-9">
              <h5 className="mb-1">
                {data?.data?.user?.firstName} {data?.data?.user?.lastName}
                {data?.data?.user?.accountVerified && (
                  <BadgeCheck className="inline-block size-4 text-sky-500 fill-sky-100 dark:fill-custom-500/20">
                    {" "}
                  </BadgeCheck>
                )}
              </h5>
              <div className="flex gap-3 mb-4">
                <p className="text-slate-500 dark:text-zink-200">
                  <UserCircle className="inline-block size-4 ltr:mr-1 rtl:ml-1 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-500"></UserCircle>{" "}
                  {data?.data?.user?.role}
                </p>
                <p className="text-slate-500 dark:text-zink-200">
                  <MapPin className="inline-block size-4 ltr:mr-1 rtl:ml-1 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-500"></MapPin>{" "}
                  {data?.data?.mynda?.state}, {data?.data?.mynda?.lga}
                </p>
              </div>
              <ul className="flex flex-wrap gap-3 mt-4 text-center divide-x divide-slate-200 dark:divide-zink-500 rtl:divide-x-reverse">
                <li className="px-5">
                  <p className="text-slate-500 dark:text-zink-200">
                    {data?.data?.user?.email}
                  </p>
                </li>
                <li className="px-5">
                  <p className="text-slate-500 dark:text-zink-200">
                    {data?.data?.user?.phoneNumber}
                  </p>
                </li>
              </ul>
              <p className="mt-4 text-slate-500 dark:text-zink-200">
                {data?.data?.mynda?.about}
              </p>
            </div>
          </div>

          {["In Review", "Pending"].includes(data?.data?.user?.kycStatus!) && (
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleApprove}
                disabled={verifying}
                className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                {verifying ? "Processing..." : "Approve"}
              </button>

              <button
                type="button"
                onClick={handleReject}
                disabled={verifying}
                className="text-white transition-all duration-200 ease-linear btn bg-red-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                Reject
              </button>
            </div>
          )}
          <div>
            <button
              type="button"
              onClick={toggleShowMessage}
              disabled={verifying}
              className="text-white mt-8 transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            >
              {verifying ? "Processing..." : "Send message"}
            </button>
          </div>
        </div>
      </div>

      <Modal
        show={showComment}
        onHide={toggleComment}
        id="defaultModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-16">Reason for rejection</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 overflow-y-auto">
          <form action="#!" onSubmit={handleVerification}>
            <div className="mb-3">
              <label className="inline-block mb-2 text-base font-medium">
                Additional Comment
              </label>
              <textarea
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter chnages needed to be made"
                name="comment"
                onChange={({ target }) => setComment(target.value)}
                value={comment}
                required
              />
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="reset"
                data-modal-close="addDocuments"
                className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                onClick={toggleComment}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                {verifying ? "Loading..." : "Submit"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showMessage}
        onHide={toggleShowMessage}
        id="defaultModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            Send message notification
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 overflow-y-auto">
          <form action="#!" onSubmit={handleMessage}>
            <div className="mb-3">
              <label
                htmlFor="title"
                className="inline-block mb-2 text-base font-medium"
              >
                Title
              </label>
              <input
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter notification title"
                name="title"
                id="title"
                required
              />
            </div>
            <div className="mb-3">
              <label
                htmlFor="message"
                className="inline-block mb-2 text-base font-medium"
              >
                Enter message
              </label>
              <textarea
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter notification message"
                name="message"
                id="message"
                required
              />
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="reset"
                data-modal-close="addDocuments"
                className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                onClick={toggleShowMessage}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                {messaging ? "Sending..." : "Send message"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default AccountInfo;
